import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Folder,
  File,
  Upload,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  Send,
  FolderInput,
  ArrowLeft,
  Eye,
  Mail,
  MailOpen,
  MailWarning,
  GripVertical,
  Search,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import {
  detectPreviewKind,
  loadDocumentPreview,
  releasePreview,
} from '../../utils/documentPreview';
import { useAuthStore } from '../../stores/authStore';
import PageHeading from '../../components/PageHeading';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import DocumentFloorPlanView, {
  isFloorPlanDocument,
} from '../../components/DocumentFloorPlanView';

const DEFAULT_COMPANY = 'company-001';

function formatBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateValue(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : v.slice(0, 10);
  }
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return String(v);
  }
}

function MailStatus({ status }) {
  const key = String(status || '').toLowerCase();
  if (key === 'opened') {
    return (
      <span className="inline-flex items-center gap-1" title="Opened by recipient">
        <MailOpen className="w-4 h-4 text-emerald-700" aria-label="Opened" />
        <span className="text-xs text-emerald-800">Opened</span>
      </span>
    );
  }
  if (key === 'failed') {
    return (
      <span className="inline-flex items-center gap-1" title="Send failed">
        <MailWarning className="w-4 h-4 text-red-600" aria-label="Send failed" />
        <span className="text-xs text-red-700">Failed</span>
      </span>
    );
  }
  if (key === 'sent') {
    return (
      <span className="inline-flex items-center gap-1" title="Sent — waiting for open">
        <Mail className="w-4 h-4 text-slate-700" aria-label="Sent" />
        <span className="text-xs text-slate-700">Sent</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" title="Not sent yet">
      <Mail className="w-4 h-4 text-gray-300" aria-label="Not sent" />
      <span className="text-xs text-gray-400">Not sent</span>
    </span>
  );
}

function isImageDoc(file) {
  if (!file?.id) return false;
  return detectPreviewKind(file) === 'image';
}

/** Gallery chip: show issued name like "ss 3.1-12a", never a bare "HTML". */
function galleryThumbLabel(file) {
  const raw = String(file?.name || '')
    .trim()
    .replace(/\.(html?|pdf)$/i, '');
  if (raw) {
    const head = raw.match(/^(ss\s*[\d.]+(?:-\d+)?(?:-[A-Za-z0-9]+)?)/i);
    if (head) return head[1].replace(/\s+/g, ' ').trim();
    if (raw.length <= 18) return raw;
    return `${raw.slice(0, 16)}…`;
  }
  const kind = detectPreviewKind(file);
  if (kind === 'pdf') return 'PDF';
  if (kind === 'image') return 'IMG';
  return 'File';
}

function applyLocalFolderOrder(folders, orderNames) {
  const list = Array.isArray(folders) ? folders : [];
  if (!Array.isArray(orderNames) || !orderNames.length) return list;
  const byName = new Map(list.map((f) => [String(f.name), f]));
  const out = [];
  const seen = new Set();
  for (const name of orderNames) {
    const key = String(name);
    const f = byName.get(key);
    if (f && !seen.has(key)) {
      out.push(f);
      seen.add(key);
    }
  }
  for (const f of list) {
    if (!seen.has(String(f.name))) out.push(f);
  }
  return out;
}

function companyFolderOrderKey(scopeKey, parentPath) {
  return `bwof-doc-folder-order:${scopeKey}:${parentPath || ''}`;
}

function toSortTime(v) {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Newest activity / date first; date-folder names (YYYY-MM-DD) by name desc. */
function sortNewestFirst(items) {
  return [...(items || [])].sort((a, b) => {
    const na = String(a?.name || '');
    const nb = String(b?.name || '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(na) && /^\d{4}-\d{2}-\d{2}$/.test(nb)) {
      return nb.localeCompare(na);
    }
    const tb = toSortTime(b?.updatedAt || b?.uploadDate || b?.createdAt);
    const ta = toSortTime(a?.updatedAt || a?.uploadDate || a?.createdAt);
    if (tb !== ta) return tb - ta;
    return String(a?.label || a?.name || '').localeCompare(String(b?.label || b?.name || ''));
  });
}

function matchesListSearch(item, q) {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    item?.label,
    item?.name,
    item?.notes,
    item?.category,
    item?.path,
    item?.buildingName,
  ]
    .map((x) => String(x || '').toLowerCase())
    .join(' ');
  return hay.includes(needle);
}

function Documents() {
  const { id: buildingIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const isBuilding = !!buildingIdParam;
  const companyId = user?.company_id || user?.companyId || DEFAULT_COMPANY;
  const scopeKey = isBuilding ? `building-${buildingIdParam}` : `company-${companyId}`;
  const base = isBuilding
    ? `/buildings/${buildingIdParam}/documents`
    : `/companies/${companyId}/documents`;

  const [cwd, setCwdState] = useState(() => searchParams.get('path') || '');
  const setCwd = (path) => {
    const nextPath = path || '';
    setCwdState(nextPath);
    if (!isBuilding) {
      const next = new URLSearchParams();
      if (nextPath) next.set('path', nextPath);
      setSearchParams(next, { replace: true });
    }
  };

  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploadDate, setUploadDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [uploadRelated, setUploadRelated] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [viewer, setViewer] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveCategory, setMoveCategory] = useState('Other');
  const [moveDate, setMoveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sendTarget, setSendTarget] = useState(null);
  const [sendTo, setSendTo] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [mailLog, setMailLog] = useState(null);
  const [thumbUrls, setThumbUrls] = useState({});
  const thumbUrlsRef = useRef({});
  const thumbStripRef = useRef(null);
  const selectedThumbRef = useRef(null);
  const galleryFilesRef = useRef([]);
  const viewerRef = useRef(null);
  const [folderOrderOverride, setFolderOrderOverride] = useState(null);
  const [dragFolderName, setDragFolderName] = useState(null);
  const [dragOverFolderName, setDragOverFolderName] = useState(null);
  const dragFolderRef = useRef(null);

  const { data: categoriesData } = useQuery({
    queryKey: ['docs-categories', scopeKey],
    queryFn: async () => (await api.get(`${base}/categories`)).data,
  });

  const { data: browse, isLoading } = useQuery({
    queryKey: ['docs-browse', scopeKey, cwd],
    queryFn: async () =>
      (await api.get(`${base}/browse`, { params: { path: cwd || '' } })).data,
  });

  const categories = categoriesData?.categories || [];
  const activeBrowse = browse;
  /** Search only when this folder shows a file list (e.g. Form 12A aggregated). */
  const showListSearch = !isBuilding && !!cwd && !!browse?.aggregated;

  const invalidateDocs = () => {
    queryClient.invalidateQueries(['docs-browse', scopeKey]);
    queryClient.invalidateQueries(['docs-categories', scopeKey]);
  };

  const apiBaseForFile = (file) => {
    if (file?.source === 'building' && file?.buildingId) {
      return `/buildings/${file.buildingId}/documents`;
    }
    return base;
  };

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const cat = cwd.split('/')[0] || uploadCategory;
      const form = new FormData();
      form.append('file', file);
      form.append('category', cat === 'Temporary' ? 'Other' : cat);
      form.append('upload_date', uploadDate);
      const related =
        uploadRelated.trim() ||
        (cat === 'Floor-Plan' && cwd.split('/').length > 1 ? cwd.split('/')[1] : '');
      if (related) form.append('related_name', related);
      return (await api.post(`${base}/upload`, form)).data;
    },
    onSuccess: () => {
      toast.success('Uploaded to cabinet');
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Upload failed'),
  });

  const openMailLog = async (file) => {
    if (!file?.id || !isBuilding) return;
    try {
      const data = (await api.get(`${base}/${file.id}/mail-log`)).data?.data;
      setMailLog(data || null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Mail log unavailable');
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ docId, file }) => {
      const apiBase = apiBaseForFile(file || viewer?.doc);
      await api.delete(`${apiBase}/${docId}`);
    },
    onSuccess: () => {
      toast.success('Deleted');
      closeViewer();
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const moveMutation = useMutation({
    mutationFn: async ({ docId, category, upload_date, file }) => {
      const apiBase = apiBaseForFile(file || moveTarget);
      return (await api.post(`${apiBase}/${docId}/move`, { category, upload_date })).data;
    },
    onSuccess: () => {
      toast.success('Moved');
      setMoveTarget(null);
      closeViewer();
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Move failed'),
  });

  const sendMutation = useMutation({
    mutationFn: async ({ docId, to, subject, message, file }) => {
      const apiBase = apiBaseForFile(file || sendTarget);
      return (await api.post(`${apiBase}/${docId}/send`, { to, subject, message })).data;
    },
    onSuccess: (data) => {
      toast.success(data?.result?.stubbed ? 'Send queued (SMTP stub — check server log)' : 'Sent');
      setSendTarget(null);
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Send failed'),
  });

  const createFolderMutation = useMutation({
    mutationFn: async (name) =>
      (await api.post(`${base}/folders`, { parent_path: cwd || '', name })).data,
    onSuccess: () => {
      toast.success('Folder created');
      setNewFolderName('');
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Create folder failed'),
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (folderPath) =>
      api.delete(`${base}/folders`, { params: { path: folderPath } }),
    onSuccess: () => {
      toast.success('Folder deleted');
      invalidateDocs();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete folder failed'),
  });

  const parentPath = useMemo(() => {
    if (!cwd) return null;
    const parts = cwd.split('/').filter(Boolean);
    parts.pop();
    return parts.join('/');
  }, [cwd]);

  /** All managed files in the open folder — same gallery UX as photos. */
  const galleryFiles = useMemo(
    () => (activeBrowse?.files || []).filter((f) => f?.id),
    [activeBrowse?.files]
  );
  galleryFilesRef.current = galleryFiles;
  viewerRef.current = viewer;

  const galleryIndex = useMemo(() => {
    if (!viewer?.doc?.id) return -1;
    return galleryFiles.findIndex((f) => String(f.id) === String(viewer.doc.id));
  }, [viewer, galleryFiles]);

  const openFolder = (folderPath) => {
    setFolderOrderOverride(null);
    setListSearch('');
    setCwd(folderPath || '');
  };

  const goUp = () => {
    setFolderOrderOverride(null);
    setListSearch('');
    setCwd(parentPath ?? '');
  };

  useEffect(() => {
    setFolderOrderOverride(null);
    setListSearch('');
  }, [cwd, scopeKey]);

  const displayFolders = useMemo(() => {
    let list = activeBrowse?.folders || [];
    if (folderOrderOverride) {
      list = applyLocalFolderOrder(list, folderOrderOverride);
    } else if (!isBuilding) {
      try {
        const raw = localStorage.getItem(companyFolderOrderKey(scopeKey, cwd));
        if (raw) {
          const names = JSON.parse(raw);
          if (Array.isArray(names) && names.length) {
            list = applyLocalFolderOrder(list, names);
          }
        }
      } catch (_) {
        /* ignore */
      }
    }
    if (!isBuilding && !folderOrderOverride) {
      list = sortNewestFirst(list);
    }
    // Root / folder-only views: no search filter (search is for file lists).
    if (showListSearch) {
      return list.filter((f) => matchesListSearch(f, listSearch));
    }
    return list;
  }, [
    activeBrowse?.folders,
    folderOrderOverride,
    isBuilding,
    scopeKey,
    cwd,
    listSearch,
    showListSearch,
  ]);

  const displayFiles = useMemo(() => {
    const list = sortNewestFirst(activeBrowse?.files || []);
    if (!showListSearch) return list;
    return list.filter((f) => matchesListSearch(f, listSearch));
  }, [activeBrowse?.files, listSearch, showListSearch]);

  const persistFolderOrder = async (orderNames) => {
    setFolderOrderOverride(orderNames);
    if (isBuilding) {
      try {
        await api.put(`${base}/folder-order`, {
          parent_path: cwd || '',
          order: orderNames,
        });
        invalidateDocs();
      } catch (e) {
        toast.error(e.response?.data?.error || 'Could not save folder order');
      }
      return;
    }
    try {
      localStorage.setItem(companyFolderOrderKey(scopeKey, cwd), JSON.stringify(orderNames));
    } catch (_) {
      /* ignore */
    }
  };

  const reorderFolderTo = async (fromName, toName) => {
    if (!fromName || !toName || fromName === toName) return;
    const list = [...displayFolders];
    const fromIdx = list.findIndex((f) => String(f.name) === String(fromName));
    const toIdx = list.findIndex((f) => String(f.name) === String(toName));
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...list];
    const [item] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, item);
    await persistFolderOrder(next.map((f) => f.name));
  };

  const releaseThumbUrls = (urls) => {
    Object.values(urls || {}).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {
        /* ignore */
      }
    });
  };

  const closeViewer = () => {
    releasePreview(viewerRef.current);
    releaseThumbUrls(thumbUrlsRef.current);
    thumbUrlsRef.current = {};
    setThumbUrls({});
    setViewer(null);
  };

  const openFile = async (file) => {
    if (!file?.id) {
      toast.error('File is on disk only — re-upload to manage it');
      return;
    }
    try {
      const preview = await loadDocumentPreview(api, apiBaseForFile(file), file.id, file);
      setViewer((prev) => {
        if (prev && prev.doc?.id !== file.id) releasePreview(prev);
        return { doc: file, ...preview };
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Cannot open file');
    }
  };

  const showGalleryFile = async (index) => {
    const list = galleryFilesRef.current;
    if (index < 0 || index >= list.length) return;
    const file = list[index];
    if (!file?.id) return;
    try {
      const preview = await loadDocumentPreview(api, apiBaseForFile(file), file.id, file);
      setViewer((prev) => {
        releasePreview(prev);
        return { doc: file, ...preview };
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Cannot open file');
    }
  };

  const showPrevFile = () => {
    if (galleryIndex <= 0) return;
    showGalleryFile(galleryIndex - 1);
  };

  const showNextFile = () => {
    if (galleryIndex < 0 || galleryIndex >= galleryFiles.length - 1) return;
    showGalleryFile(galleryIndex + 1);
  };

  const galleryIdsKey = galleryFiles.map((f) => f.id).join(',');
  const galleryNav = galleryFiles.length > 1 && galleryIndex >= 0;

  // Load image thumbnails for the folder strip while the viewer is open
  useEffect(() => {
    if (!viewer || !galleryFiles.length) return undefined;
    let cancelled = false;
    const files = galleryFiles.filter((f) => isImageDoc(f));
    const loadThumbs = async () => {
      for (const file of files) {
        if (cancelled) break;
        if (thumbUrlsRef.current[file.id]) continue;
        try {
          const apiBase = apiBaseForFile(file);
          const res = await api.get(`${apiBase}/${file.id}/view`, { responseType: 'blob' });
          if (cancelled) break;
          const url = URL.createObjectURL(res.data);
          if (thumbUrlsRef.current[file.id]) {
            URL.revokeObjectURL(url);
            continue;
          }
          thumbUrlsRef.current = { ...thumbUrlsRef.current, [file.id]: url };
          setThumbUrls({ ...thumbUrlsRef.current });
        } catch (_) {
          /* skip broken thumb */
        }
      }
    };
    loadThumbs();
    return () => {
      cancelled = true;
    };
  }, [!!viewer, base, galleryIdsKey]);

  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const list = galleryFilesRef.current;
        const cur = viewerRef.current;
        const idx = list.findIndex((f) => String(f.id) === String(cur?.doc?.id));
        if (idx > 0) showGalleryFile(idx - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const list = galleryFilesRef.current;
        const cur = viewerRef.current;
        const idx = list.findIndex((f) => String(f.id) === String(cur?.doc?.id));
        if (idx >= 0 && idx < list.length - 1) showGalleryFile(idx + 1);
      } else if (e.key === 'Escape') {
        closeViewer();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [!!viewer]);

  useEffect(() => {
    if (selectedThumbRef.current) {
      selectedThumbRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [galleryIndex]);

  const downloadFile = async (file) => {
    if (!file?.id) return;
    try {
      const apiBase = apiBaseForFile(file);
      const res = await api.get(`${apiBase}/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Download failed');
    }
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    files.forEach((f) => uploadMutation.mutate(f));
  };

  const scopeTitle = isBuilding
    ? browse?.building?.name || categoriesData?.building?.name || `Building #${buildingIdParam}`
    : browse?.company?.name || categoriesData?.company?.name || companyId;

  const crumbs = useMemo(() => {
    if (isBuilding) {
      return browse?.breadcrumbs || [{ name: 'Documents', path: '' }];
    }
    return browse?.breadcrumbs || [{ name: 'Company files', path: '' }];
  }, [isBuilding, browse?.breadcrumbs]);

  const headerFolderTitle = useMemo(() => {
    if (isBuilding) return 'Building Documents';
    if (!cwd) return 'Company files';
    const last = crumbs[crumbs.length - 1];
    return last?.name || 'Company files';
  }, [isBuilding, cwd, crumbs]);

  const headingProps = {
    title: headerFolderTitle,
    infoTitle: isBuilding ? 'Building documents' : 'Company files',
    infoChildren: (
      <div className="space-y-2 text-sm">
        <p>
          {isBuilding ? (
            <>
              Offline cabinet for <span className="font-medium">{scopeTitle}</span>. Company files
              gather this company&apos;s forms from every building.
            </>
          ) : (
            <>
              Company files for <span className="font-medium">{scopeTitle}</span> — open a folder
              (e.g. Form 12A) to see that form from every building, newest first.
            </>
          )}
        </p>
        <p>
          Path:{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">
            {isBuilding
              ? '{Company}/buildings/{Building}/document/{Category}/{date}/'
              : '{Company}/document/{Category}/ (all buildings)'}
          </code>
        </p>
      </div>
    ),
  };

  return (
    <div className="space-y-4">
      {isBuilding ? (
        <BuildingPageHeading
          buildingId={buildingIdParam}
          buildingName={scopeTitle}
          {...headingProps}
        />
      ) : (
        <PageHeading {...headingProps} />
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200 bg-slate-50">
          {isBuilding && cwd === '' ? (
            <button
              type="button"
              onClick={() => navigate(`/buildings/${buildingIdParam}`)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <button
              type="button"
              disabled={cwd === ''}
              onClick={goUp}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-gray-300 bg-white disabled:opacity-40 hover:bg-gray-50"
            >
              <ArrowLeft className="w-4 h-4" />
              Up
            </button>
          )}

          <nav className="flex flex-wrap items-center gap-1 text-sm min-w-0 flex-1">
            {crumbs.map((c, i) => (
              <span key={`${c.path}-${i}`} className="inline-flex items-center gap-1 min-w-0">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                <button
                  type="button"
                  onClick={() => openFolder(c.path)}
                  className={`truncate max-w-[160px] ${
                    i === crumbs.length - 1
                      ? 'font-semibold text-gray-900'
                      : 'text-primary-700 hover:underline'
                  }`}
                >
                  {c.name}
                </button>
              </span>
            ))}
          </nav>

          {showListSearch ? (
            <div className="relative min-w-[10rem] max-w-xs flex-1 sm:flex-none">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Search this list…"
                className="w-full text-sm border border-gray-300 rounded pl-8 pr-2 py-1.5 bg-white"
              />
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            {(activeBrowse?.folders || []).length > 1 ? (
              <span className="hidden sm:inline text-xs text-gray-500 mr-1">
                Drag ≡ to reorder folders
              </span>
            ) : null}
            {!cwd && (
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                title="Upload category"
              >
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            )}
            <input
              type="date"
              value={uploadDate}
              onChange={(e) => setUploadDate(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
              title="Date folder for new uploads"
            />
            {(uploadCategory === 'Floor-Plan' || cwd.split('/')[0] === 'Floor-Plan') && (
              <input
                type="text"
                value={uploadRelated}
                onChange={(e) => setUploadRelated(e.target.value)}
                placeholder="Floor plan name"
                className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white min-w-[10rem]"
                title="Related floor-plan name (folder under Floor Plan)"
              />
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            {isBuilding ? (
              <>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="New folder"
                  className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white w-36"
                />
                <button
                  type="button"
                  disabled={!newFolderName.trim() || createFolderMutation.isPending}
                  onClick={() => createFolderMutation.mutate(newFolderName.trim())}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  New folder
                </button>
              </>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={onPickFiles}
            />
          </div>
        </div>

        {/* Listing */}
        <div className="min-h-[360px]">
          {isLoading ? (
            <p className="p-8 text-sm text-gray-500">Loading cabinet…</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium w-36">Date</th>
                  <th className="px-4 py-2 font-medium w-40">Actions</th>
                  <th className="px-4 py-2 font-medium min-w-[9rem]">Mail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayFolders.map((f) => (
                  <tr
                    key={`folder-${f.path}`}
                    className={`hover:bg-amber-50/60 cursor-pointer ${
                      dragFolderName === f.name ? 'opacity-40' : ''
                    } ${
                      dragOverFolderName === f.name
                        ? 'ring-2 ring-inset ring-primary-400 bg-primary-50/40'
                        : ''
                    }`}
                    onDoubleClick={() => openFolder(f.path)}
                    onDragOver={(e) => {
                      if (!dragFolderRef.current) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOverFolderName !== f.name) setDragOverFolderName(f.name);
                    }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      const from =
                        dragFolderRef.current || e.dataTransfer.getData('text/plain');
                      setDragFolderName(null);
                      setDragOverFolderName(null);
                      dragFolderRef.current = null;
                      if (!from || String(from) === String(f.name)) return;
                      await reorderFolderTo(from, f.name);
                    }}
                    onDragEnd={() => {
                      setDragFolderName(null);
                      setDragOverFolderName(null);
                      dragFolderRef.current = null;
                    }}
                  >
                    <td className="px-4 py-2.5">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          draggable
                          title="Drag to reorder folders"
                          className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-grab active:cursor-grabbing shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            dragFolderRef.current = String(f.name);
                            setDragFolderName(String(f.name));
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(f.name));
                          }}
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-left font-medium text-gray-900"
                          onClick={() => openFolder(f.path)}
                        >
                          <Folder className="w-5 h-5 text-amber-600 shrink-0" />
                          <span>
                            {f.label || f.name}
                            {f.fileCount != null && (
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                ({f.fileCount} files)
                              </span>
                            )}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 tabular-nums">
                      {formatDateValue(f.updatedAt) || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        className="text-primary-700 hover:underline"
                        onClick={() => openFolder(f.path)}
                      >
                        Open
                      </button>
                      {isBuilding && !f.isSystem ? (
                        <button
                          type="button"
                          className="ml-2 text-red-600 hover:underline"
                          onClick={() => {
                            if (window.confirm(`Delete folder “${f.label || f.name}”?`)) {
                              deleteFolderMutation.mutate(f.path);
                            }
                          }}
                        >
                          Delete
                        </button>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5" />
                  </tr>
                ))}

                {displayFiles.map((file) => (
                  <tr
                    key={file.id ? `file-${file.id}` : `disk-${file.path}`}
                    className="hover:bg-blue-50/40"
                    onDoubleClick={() => openFile(file)}
                  >
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 text-left min-w-0"
                        onClick={() => openFile(file)}
                      >
                        <File className="w-5 h-5 text-slate-500 shrink-0" />
                        <span className="min-w-0">
                          <span className="font-medium text-gray-900 block truncate">
                            {file.name}
                          </span>
                          {file.buildingName ? (
                            <span className="text-xs text-gray-500 block truncate">
                              {file.buildingName}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 tabular-nums">
                      {formatDateValue(file.updatedAt || file.createdAt || file.uploadDate) || '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-2 text-xs">
                        <button
                          type="button"
                          className="text-primary-700 hover:underline inline-flex items-center gap-0.5"
                          onClick={() => openFile(file)}
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        {file.id && (
                          <button
                            type="button"
                            className="text-gray-700 hover:underline inline-flex items-center gap-0.5"
                            onClick={() => downloadFile(file)}
                          >
                            <Download className="w-3.5 h-3.5" /> DL
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {file.id ? (
                        <button
                          type="button"
                          className="flex flex-wrap items-center gap-1.5 text-left hover:bg-gray-50 rounded px-1 py-0.5 -mx-1"
                          title="Mail status — click for log"
                          onClick={() => openMailLog(file)}
                        >
                          <MailStatus status={file.mailStatus} />
                          {file.mailTo ? (
                            <span className="text-xs text-primary-700 truncate max-w-[10rem]">
                              {file.mailTo}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        <MailStatus status={file.mailStatus} />
                      )}
                    </td>
                  </tr>
                ))}

                {!isLoading &&
                  displayFolders.length === 0 &&
                  displayFiles.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                        {listSearch.trim() ? (
                          'No matches in this list.'
                        ) : (
                          <>
                            Empty folder. Use <strong>Upload</strong> to add offline
                            documents.
                          </>
                        )}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Viewer modal */}
      {viewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b shrink-0">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate">{viewer.doc.name}</h3>
                <p className="text-xs text-gray-500">
                  {viewer.doc.buildingName ? `${viewer.doc.buildingName} · ` : ''}
                  {viewer.doc.category} · {formatDateValue(viewer.doc.uploadDate)} ·{' '}
                  {formatBytes(viewer.doc.size)}
                  {galleryIndex >= 0 ? (
                    <span className="ml-2 tabular-nums">
                      · {galleryIndex + 1} / {galleryFiles.length}
                    </span>
                  ) : null}
                </p>
              </div>
              <button type="button" onClick={closeViewer} className="p-1 text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden bg-gray-100 min-h-[240px] flex flex-col">
              <div className="relative flex-1 flex items-center justify-center p-4 min-h-0">
                {viewer.kind === 'image' &&
                viewer.blobUrl &&
                isBuilding &&
                isFloorPlanDocument(viewer.doc) ? (
                  <DocumentFloorPlanView
                    buildingId={buildingIdParam}
                    doc={viewer.doc}
                    imageUrl={viewer.blobUrl}
                  />
                ) : null}
                {viewer.kind === 'image' &&
                viewer.blobUrl &&
                !(isBuilding && isFloorPlanDocument(viewer.doc)) ? (
                  <img
                    src={viewer.blobUrl}
                    alt={viewer.doc.name}
                    className="max-w-full max-h-[48vh] object-contain"
                  />
                ) : null}
                {viewer.kind === 'pdf' && viewer.blobUrl && (
                  <iframe title={viewer.doc.name} src={viewer.blobUrl} className="w-full h-[60vh] bg-white" />
                )}
                {viewer.kind === 'html' && viewer.html && (
                  <iframe
                    title={viewer.doc.name}
                    srcDoc={viewer.html}
                    className="w-full h-[60vh] bg-white border-0"
                    sandbox="allow-same-origin"
                  />
                )}
                {viewer.kind === 'other' && (
                  <p className="text-sm text-gray-600">
                    Preview not available for this type. Use Download.
                  </p>
                )}
                {galleryNav ? (
                  <>
                    <button
                      type="button"
                      disabled={galleryIndex <= 0}
                      onClick={showPrevFile}
                      className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 border border-slate-200 shadow p-2 text-slate-800 hover:bg-white disabled:opacity-30 disabled:pointer-events-none z-20"
                      aria-label="Previous file"
                      title="Previous file"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      type="button"
                      disabled={galleryIndex >= galleryFiles.length - 1}
                      onClick={showNextFile}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 border border-slate-200 shadow p-2 text-slate-800 hover:bg-white disabled:opacity-30 disabled:pointer-events-none z-20"
                      aria-label="Next file"
                      title="Next file"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                ) : null}
              </div>
              {galleryNav ? (
                <div className="shrink-0 border-t bg-slate-50 px-3 py-2">
                  <div
                    ref={thumbStripRef}
                    className="flex items-end gap-2 overflow-x-auto pb-1"
                  >
                    {galleryFiles.map((file, idx) => {
                      const selected = idx === galleryIndex;
                      const thumb = thumbUrls[file.id];
                      const image = isImageDoc(file);
                      return (
                        <button
                          key={file.id}
                          type="button"
                          ref={selected ? selectedThumbRef : undefined}
                          onClick={() => showGalleryFile(idx)}
                          title={file.name}
                          className={`shrink-0 rounded border overflow-hidden bg-white transition ${
                            selected
                              ? 'border-primary-600 ring-2 ring-primary-500 shadow-md'
                              : 'border-slate-200 hover:border-slate-400 opacity-90'
                          }`}
                          style={{
                            width: selected ? 88 : 64,
                            height: selected ? 72 : 52,
                          }}
                        >
                          {image && thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : image ? (
                            <span className="flex h-full items-center justify-center text-[10px] text-gray-400">
                              …
                            </span>
                          ) : (
                            <span className="flex h-full flex-col items-center justify-center gap-0.5 px-1 text-slate-700">
                              <File className="w-3.5 h-3.5 shrink-0 text-slate-500" />
                              <span
                                className="text-[9px] font-semibold leading-tight text-center break-words max-w-full px-0.5"
                                title={file.name}
                              >
                                {galleryThumbLabel(file)}
                              </span>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2 px-4 py-3 border-t bg-slate-50 shrink-0">
              <button
                type="button"
                onClick={() => downloadFile(viewer.doc)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
              >
                <Download className="w-4 h-4" /> Download
              </button>
              <button
                type="button"
                onClick={() => {
                  setSendTarget(viewer.doc);
                  setSendTo('');
                  setSendSubject(`${isBuilding ? '[Documents]' : '[Company files]'} ${viewer.doc.name}`);
                  setSendMessage('');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
              >
                <Send className="w-4 h-4" /> Send
              </button>
              <button
                type="button"
                onClick={() => {
                  setMoveTarget(viewer.doc);
                  setMoveCategory(viewer.doc.category || 'Other');
                  setMoveDate(formatDateValue(viewer.doc.uploadDate) || uploadDate);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
              >
                <FolderInput className="w-4 h-4" /> Move
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!window.confirm(`Delete "${viewer.doc.name}"?`)) return;
                  deleteMutation.mutate({ docId: viewer.doc.id, file: viewer.doc });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-red-200 text-red-700 bg-white hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move dialog */}
      {moveTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-4 space-y-3">
            <h4 className="font-semibold">Move file</h4>
            <p className="text-sm text-gray-600 truncate">{moveTarget.name}</p>
            <label className="block text-sm">
              Category
              <select
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={moveCategory}
                onChange={(e) => setMoveCategory(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Date folder
              <input
                type="date"
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={moveDate}
                onChange={(e) => setMoveDate(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={() => setMoveTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white"
                disabled={moveMutation.isPending}
                onClick={() =>
                  moveMutation.mutate({
                    docId: moveTarget.id,
                    category: moveCategory,
                    upload_date: moveDate,
                    file: moveTarget,
                  })
                }
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {mailLog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold">Mail log</h4>
              <button type="button" onClick={() => setMailLog(null)} className="text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-800 truncate">{mailLog.name}</p>
            <dl className="text-sm space-y-1.5">
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Status</dt>
                <dd className="font-medium text-gray-900">{mailLog.status || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">To</dt>
                <dd className="text-gray-900 break-all">{mailLog.to || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Subject</dt>
                <dd className="text-gray-900">{mailLog.subject || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Sent</dt>
                <dd className="text-gray-900 tabular-nums">
                  {mailLog.sentAt ? String(mailLog.sentAt).replace('T', ' ').slice(0, 19) : '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">Opened</dt>
                <dd className="text-gray-900 tabular-nums">
                  {mailLog.openedAt ? String(mailLog.openedAt).replace('T', ' ').slice(0, 19) : '—'}
                </dd>
              </div>
            </dl>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded"
                onClick={() => setMailLog(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send dialog */}
      {sendTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-4 space-y-3">
            <h4 className="font-semibold">Send file</h4>
            <p className="text-sm text-gray-600 truncate">{sendTarget.name}</p>
            <label className="block text-sm">
              To (email)
              <input
                type="email"
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                placeholder="name@example.com"
              />
            </label>
            <label className="block text-sm">
              Subject
              <input
                type="text"
                className="mt-1 w-full border rounded px-2 py-1.5"
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Message
              <textarea
                className="mt-1 w-full border rounded px-2 py-1.5"
                rows={3}
                value={sendMessage}
                onChange={(e) => setSendMessage(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={() => setSendTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-primary-600 text-white"
                disabled={sendMutation.isPending || !sendTo.trim()}
                onClick={() =>
                  sendMutation.mutate({
                    docId: sendTarget.id,
                    to: sendTo.trim(),
                    subject: sendSubject,
                    message: sendMessage,
                  })
                }
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documents;
